import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { LoadingDots } from '../ui/loading';

const Section = ({ title, children }) => (
  <section className="mb-8 w-full">
    <h3 className="text-lg w-full text-left font-semibold text-[#005f73] border-b border-gray-200 pb-2 mb-4">
      {title}
    </h3>
    <div className="space-y-4">{children}</div>
  </section>
);

const DetailItem = ({ label, value, color }) => (
  <div>
    <h4 className="text-sm font-medium text-left text-gray-700">{label}</h4>
    <p className="text-gray-500 text-sm text-left">{value || 'N/A'}</p>
  </div>
);

const StatusItem = ({ label, value, isPositive, defaultItem }) => (
  <div className='flex items-start flex-col'>
    <h4 className="text-sm font-medium text-gray-500">{label}</h4>
    <span
      className={`px-3 py-1 rounded-full text-sm font-medium mt-1 text-left w-full ${defaultItem ?
        'bg-[#e6f3f7] text-[#005f73]'
        :
        (isPositive
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700')
        }`}
    >
      {value}
    </span>
  </div>
);

const getArticle = (descricao) => {
  if (!descricao) return '';
  const match = descricao.match(/art\. \d+/i);
  return match ? `(${match[0]})` : '';
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    timeZone: 'UTC',
  });
};

const getSidebarTitle = (contexto) => {
  if (!contexto) return 'Sem descrição';
  let title = contexto.split('.')[0];
  return title;
};

const PresumedCredit = () => {
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState([]);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [error, setError] = useState(null);
  const [loadingContent, setLoadingContent] = useState(false);

  useEffect(() => {
    async function handleResult() {
      setLoading(true);
      setCredits([]);
      setSelectedCredit(null);
      setError(null);

      try {
        const { data, status } = await api.get(`credito/presumido/consultar`);
        if (status !== 200) {
          throw new Error('Erro ao buscar cClassTrib');
        }
        const creditList = data?.creditos_presumidos || [];

        setCredits(creditList);

        if (creditList.length > 0) {
          setSelectedCredit(creditList[0]);
        }
      } catch (error) {
        console.error(error);
        setError('Erro ao carregar dados.');
      } finally {
        setLoading(false);
      }
    }

    handleResult();
  }, []);

  useEffect(() => {
    if (loadingContent) {
      setTimeout(() => {
        setLoadingContent(false);
      }, 400);
    }
  }, [loadingContent])

  if (loading) {
    return <div className="min-h-[50vh] flex justify-center items-center">
      <LoadingDots />
    </div>;
  }

  if (error) {
    return <div className="p-8 text-center text-red-600">{error}</div>;
  }

  if (!credits.length) {
    return (
      <div className="p-8 text-center">Nenhum crédito presumido encontrado.</div>
    );
  }

  return (
    <section className="flex w-full scroll">
      <aside className="w-1/4 max-w-xs p-4 bg-white mr-8 border border-gray-200 rounded-xl sticky overflow-y-auto scroll" style={{
        maxHeight: `calc(100vh - (154px + 116px))`
      }}>
        <h2 className="text-sm font-medium text-gray-500 mb-4 px-3 w-full">
          Listagem dos créditos presumidos
        </h2>
        <nav className="flex flex-col space-y-1">
          {credits.map((credit) => (
            <button
              key={credit.id}
              onClick={() => {
                setSelectedCredit(credit)
                setLoadingContent(true);
              }}
              className={`p-3 text-left rounded-lg flex flex-col gap-2 transition-colors w-full ${selectedCredit?.id === credit.id
                ? 'bg-[#e6f3f7] font-semibold border border-[#b3d1d9] text-[#005f73]'
                : 'text-gray-700 bg-[#f4f7f6] hover:text-[#005f73]'
                }`}
            >
              <div className='flex flex-row justify-between items-center flex-wrap'>
                <span className="font-semibold block text-[12px]">
                  Cód: {credit.ccredpres}
                </span>
                <span className="font-semibold block text-[12px]">
                  cClass: {credit.cclass_nota_referenciada}
                </span>
              </div>
              <span className="text-xs">
                {credit.paliq_credpres_cbs}
              </span>
              <span className="text-xs">
                {getSidebarTitle(credit.contexto_aplicacao)}
              </span>
            </button>
          ))}
        </nav>
      </aside>
      <main className="flex-1 p-8 overflow-y-auto border border-gray-200 bg-white rounded-xl scroll">
        {
          loadingContent ? (
            <div className="min-h-[55vh] flex justify-center items-center">
              <LoadingDots />
            </div>
          ) :
            (selectedCredit ? (
              <CreditDetailsView credit={selectedCredit} />
            )

              : (
                <div className="text-gray-500">
                  Selecione um crédito para ver os detalhes.
                </div>
              ))}
      </main>
    </section>
  );
};

const CreditDetailsView = ({ credit }) => {
  const boolToText = (value) => (value === '1' ? 'Sim' : 'Não');
  const exigeHabilitacaoText = credit.exige_habilitacao.startsWith('Não')
    ? 'Não'
    : 'Sim';

  return (
    <div className="max-w-4xl">
      <h1 className="text-md font text-[#005f73]  w-full text-left border-b border-gray-200 pb-2 mb-4">
        <strong> Cód: {credit.ccredpres}</strong> - {getSidebarTitle(credit.contexto_aplicacao)}
      </h1>
      <Section title="Contexto e aplicação">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DetailItem label="Contexto" value={credit.contexto_aplicacao} />
          <DetailItem label="Tipo fornecedor" value={credit.tipo_fornecedor} />
          <DetailItem label="Tipo operação" value={credit.tipo_operacao} />
        </div>
      </Section>
      <Section title="Regras de apropriação">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatusItem
            label="Forma apropriação"
            defaultItem
            value={credit.forma_apropriacao}
          />
          <StatusItem
            label="Exige habilitação?"
            value={exigeHabilitacaoText}
            isPositive={exigeHabilitacaoText === 'Sim'}
          />
          <StatusItem
            label="Apropria via NF?"
            value={`${boolToText(credit.apropria_via_nf)} (${credit.apropria_via_nf
              })`}
            isPositive={credit.apropria_via_nf === '1'}
          />
          <StatusItem
            label="Apropria via evento?"
            value={`${boolToText(credit.apropria_via_evento)} (${credit.apropria_via_evento
              })`}
            isPositive={credit.apropria_via_evento === '1'}
          />
        </div>
      </Section>

      <Section title="Alíquotas e base legal">
        <div className="mb-4">

          <blockquote className="text-gray-700 text-sm italic border-l-4 border-[#005f73]  text-left pl-4 py-4 bg-[#f4f7f6]">
            <h4 className="text-sm font-semibold text-left  mb-1 text-[#005f73]">
              LC 214/25 {getArticle(credit.descricao)}
            </h4>
            "{credit.lc_214_2025}"
          </blockquote>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <DetailItem label="Alíquota IBS" value={credit.aliq_ibs} />
          <DetailItem label="Alíquota CBS" value={credit.aliq_cbs} />
          <DetailItem label="% Alíquota" value={credit.paliq_credpres_cbs} />
          <DetailItem
            label="cClass nota ref."
            value={credit.cclass_nota_referenciada}
          />
          <DetailItem
            label="Início vigência"
            value={formatDate(credit.dtinivig)}
          />
        </div>
      </Section>

      <Section title="Exemplo prático">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
          <span className="text-yellow-600 text-xl pt-1">💡</span>
          <div>
            <h4 className="font-semibold text-yellow-800">Exemplo:</h4>
            <p className="text-yellow-700">{credit.exemplo_pratico}</p>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default PresumedCredit;